"use client";

import { useMemo, useState, useSyncExternalStore, useTransition } from "react";
import { useDayOutClock } from "@/app/_components/companion/DayOutSelection";
import { dayOutLocal, dayOutParam, dayOutPlannerTiming, dayOutRouteMatches, dayOutSportLabel, type DayOut } from "@/lib/companion/day-out";
import { readSavedRoute, savedRouteSnapshot, subscribeSavedRoute } from "@/lib/planner/saved-route";
import { normaliseMeetupInput } from "@/lib/companion/people-loop";
import { zonedLocalDateTimeToUtc } from "@/lib/public-events/time";
import {
  cancelSquadMeetup,
  saveSquadMeetup,
  updateSquadMeetupRsvp,
} from "@/app/actions/companion-people";
import type { CompanionMeetup, CompanionPeopleSquad, SquadMeetupRsvpState } from "@/lib/companion/people-loop";

export function CompanionMeetups({
  meetups,
  squads,
  selectedDate,
  selectedSquadId,
  timezone,
  defaultPlace,
  unavailable,
  viewerUserId,
  dayOut = null,
  readOnly = false,
}: {
  meetups: CompanionMeetup[];
  squads: CompanionPeopleSquad[];
  selectedDate: string;
  selectedSquadId: string | null;
  timezone: string;
  defaultPlace: { slug: string | null; name: string } | null;
  unavailable: boolean;
  viewerUserId: string | null;
  dayOut?: DayOut | null;
  readOnly?: boolean;
}) {
  const [pending, startTransition] = useTransition();
  const [message, setMessage] = useState<string | null>(null);
  const [labelEdit, setLabel] = useState<string | null>(null);
  const [timeEdit, setTime] = useState<string | null>(null);
  const [squadEdit, setSquadId] = useState<string | null>(null);
  const label = labelEdit ?? (dayOut ? `${dayOutSportLabel(dayOut.sport)} at ${defaultPlace?.name ?? "the meeting place"}`.slice(0, 80) : "");
  const time = timeEdit ?? (dayOut ? dayOutLocal(dayOut).startTime : "");
  const squadId = squadEdit ?? selectedSquadId ?? squads[0]?.id ?? "";
  const [review, setReview] = useState<{ input: Parameters<typeof saveSquadMeetup>[0]; context: string; audience: string } | null>(null);
  const now = useDayOutClock();
  const snapshot = useSyncExternalStore(subscribeSavedRoute, savedRouteSnapshot, () => null);
  const savedRoute = useMemo(() => readSavedRoute(snapshot), [snapshot]);
  const context = JSON.stringify([dayOutParam(dayOut), selectedDate, timezone, defaultPlace]);
  const selectionUnavailable = Boolean(dayOut && (!dayOutPlannerTiming(dayOut, timezone, now)
    || dayOut.journeyId && !dayOutRouteMatches(dayOut, savedRoute)));


  function rsvp(meetup: CompanionMeetup, state: SquadMeetupRsvpState) {
    startTransition(async () => {
      const result = await updateSquadMeetupRsvp({ squadId: meetup.squadId, meetupId: meetup.id, state });
      setMessage(result.ok ? "Meet-up response updated." : result.error);
    });
  }

  function prepareReview(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!defaultPlace || selectionUnavailable || unavailable) return;
    const input = { squadId, targetLocalDate: selectedDate, timezone, timeLocal: time || null, label,
      locationType: "saved_place", placeSlug: defaultPlace.slug, placeName: defaultPlace.name,
      latitude: null, longitude: null, note: null };
    const parsed = normaliseMeetupInput(input);
    const audience = squads.find((squad) => squad.id === squadId)?.name;
    if (!parsed.ok || !audience) { setMessage(parsed.ok ? "Choose a Squad." : parsed.error); return; }
    if (time && !zonedLocalDateTimeToUtc(`${selectedDate}T${time}`, timezone)) {
      setMessage("That local time is ambiguous or unavailable. Choose another time."); return;
    }
    setMessage(null);
    setReview({ input, context, audience });
  }

  function confirmReview() {
    if (!review || readOnly || pending) return;
    if (review.context !== context || selectionUnavailable || unavailable
      || dayOut && (!dayOutPlannerTiming(dayOut, timezone) || dayOut.journeyId && !dayOutRouteMatches(dayOut, readSavedRoute()))) {
      setMessage("The selected plan or day has changed. Review fresh conditions and meet-up details first.");
      setReview(null); return;
    }
    const parsed = normaliseMeetupInput(review.input);
    if (!parsed.ok) { setMessage(parsed.error); setReview(null); return; }
    startTransition(async () => {
      try {
        const result = await saveSquadMeetup(review.input);
        setMessage(result.ok ? "Meet-up saved privately to your Squad." : result.error);
        if (result.ok) setReview(null);
      } catch {
        setMessage("The save could not be confirmed. Check the meet-up list before trying again; your draft is still here.");
      }
    });
  }

  function cancel(meetup: CompanionMeetup) {
    startTransition(async () => {
      const result = await cancelSquadMeetup({
        squadId: meetup.squadId,
        meetupId: meetup.id,
      });
      setMessage(result.ok ? "Meet-up cancelled." : result.error);
    });
  }

  return (
    <section aria-labelledby="people-meetups-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-teal">Meet-ups</p>
      <h2 id="people-meetups-heading" className="mt-1 font-heading font-bold text-[26px] leading-tight text-navy">Meet here</h2>
      {unavailable ? (
        <p role="status" className="mt-3 rounded-sm border border-terracotta/35 bg-terracotta/10 px-3 py-2 text-[12px] text-navy">Meet-ups are unavailable right now.</p>
      ) : meetups.length > 0 ? (
        <ul className="mt-4 grid gap-3">
          {meetups.map((meetup) => (
            <li key={meetup.id} className="rounded-sm border border-border-light bg-warm-bg p-3">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <h3 className="text-[14px] font-semibold text-navy">{meetup.label}</h3>
                  <p className="mt-1 text-[12px] text-text-muted">{meetup.placeName ?? "Place shared with the Squad"} · {formatTime(meetup.timeLocal)}</p>
                </div>
                <p className="text-[11px] font-semibold text-teal">{meetup.rsvpCounts.in} in · {meetup.rsvpCounts.maybe} maybe</p>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {(["in", "maybe", "cant_make_it"] as const).map((state) => (
                  <button key={state} type="button" disabled={pending || readOnly} aria-pressed={meetup.viewerRsvp === state} onClick={() => rsvp(meetup, state)} className={`min-h-[44px] rounded-sm border px-3 text-[12px] font-semibold ${meetup.viewerRsvp === state ? "border-teal bg-teal text-white" : "border-border bg-white text-navy hover:border-teal/50"}`}>
                    {state === "in" ? "I'm in" : state === "maybe" ? "Maybe" : "Can't make it"}
                  </button>
                ))}
                {viewerUserId === meetup.creatorUserId ? (
                  <button
                    type="button"
                    disabled={pending || readOnly}
                    onClick={() => cancel(meetup)}
                    className="min-h-[44px] rounded-sm border border-terracotta/40 bg-white px-3 text-[12px] font-semibold text-terracotta hover:border-terracotta disabled:opacity-60"
                  >
                    Cancel meet-up
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-[13px] leading-relaxed text-text-muted">No meet-ups yet. Add a simple place and time when the plan comes together.</p>
      )}

      {selectionUnavailable && <p role="status" className="mt-3 text-sm text-terracotta">Review a current plan for today or tomorrow before arranging this meet-up.</p>}
      {squads.length > 0 && defaultPlace ? (
        <div className="mt-4 border-t border-border-light pt-4">
          <p className="mb-3 text-xs text-text-muted">{defaultPlace.name} · {selectedDate} · {timezone.replaceAll("_", " ")}</p>
          {review ? <div role="region" aria-label="Review private meet-up" className="ph-no-capture rounded-sm border border-teal/40 bg-warm-bg p-4">
            <h3 className="font-heading text-lg font-bold text-navy">Review your meet-up</h3>
            <dl className="mt-3 grid gap-2 text-sm text-navy">
              <div><dt className="font-semibold">Visible to</dt><dd>{review.audience} only</dd></div>
              <div><dt className="font-semibold">Meet-up</dt><dd>{review.input.label}</dd></div>
              <div><dt className="font-semibold">Place</dt><dd>{review.input.placeName}</dd></div>
              <div><dt className="font-semibold">Local date and time</dt><dd>{review.input.targetLocalDate}, {review.input.timeLocal ?? "Time open"} · {review.input.timezone}</dd></div>
              {dayOut && <div><dt className="font-semibold">Planned activity</dt><dd>{dayOutSportLabel(dayOut.sport)}</dd></div>}
            </dl>
            <p className="mt-3 text-xs text-text-muted">Creating this meet-up shares these details with this private Squad. It does not start tracking or live location sharing.</p>
            {readOnly && <p className="mt-2 text-xs text-text-muted">This demonstration cannot create a real meet-up.</p>}
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" disabled={pending || readOnly || selectionUnavailable} onClick={confirmReview} className="min-h-[44px] rounded-sm bg-navy px-3 text-sm font-semibold text-white disabled:opacity-50">{pending ? "Creating..." : "Create private meet-up"}</button>
              <button type="button" disabled={pending} onClick={() => setReview(null)} className="min-h-[44px] rounded-sm border border-border bg-white px-3 text-sm font-semibold text-navy">Edit details</button>
              <button type="button" disabled={pending} onClick={() => { setReview(null); setMessage("Review cancelled. Your draft has been kept and nothing was shared."); }} className="min-h-[44px] px-3 text-sm font-semibold text-text-muted">Cancel review</button>
            </div>
          </div> : <form onSubmit={prepareReview} className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-navy">Squad<select name="squad" value={squadId} onChange={(event) => setSquadId(event.target.value)} className="mt-1 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 text-sm text-navy">{squads.map((squad) => <option key={squad.id} value={squad.id}>{squad.name}</option>)}</select></label>
            <label className="text-xs font-semibold text-navy">Meet-up label<input required maxLength={80} name="label" value={label} onChange={(event) => setLabel(event.target.value)} placeholder="North car park" className="mt-1 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 text-sm text-navy" /></label>
            <label className="text-xs font-semibold text-navy">Time<input name="time" type="time" value={time} onChange={(event) => setTime(event.target.value)} className="mt-1 min-h-[44px] w-full rounded-sm border border-border bg-white px-3 text-sm text-navy" /></label>
            <button type="submit" disabled={pending || selectionUnavailable || unavailable} className="min-h-[44px] self-end rounded-sm bg-navy px-4 text-sm font-semibold text-white disabled:opacity-50">Review meet-up</button>
          </form>}
        </div>
      ) : null}
      <p aria-live="polite" className="mt-2 min-h-5 text-[12px] text-text-muted">{message}</p>
    </section>
  );
}

function formatTime(value: string | null): string {
  return value ? value.slice(0, 5) : "Time open";
}
